import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../styles/DateTimePicker.scss";
import { useLanguage } from "../context/LanguageContext";
import { formatDecimal } from "../utils/numberFormat";
import {
  buildBlockedDateSet,
  getMondayWeekdayIndex,
  normalizeDateValue,
  parseDateValue,
  resolveSelectableDate,
  toDateKey,
} from "../utils/dateUtils";
import {
  DATE_TIME_PICKER_MAX_MINUTES,
  DATE_TIME_PICKER_STEP,
} from "../utils/dateTimePickerConstants";

const DateTimePicker = ({
  onSave,
  onCancel,
  onSelectionChange,
  duration = 1.5,
  onDurationChange = () => {},
  maxDuration = 4,
  theme = "day",
  initialDate,
  autoTimeOnDaySelect = false,
  showDurationControls = true,
  showActions = false,
  actionsStatus = null,
  saveLabel,
  cancelLabel,
  isSaving = false,
  isSaveDisabled = false,
  onDragStateChange,
  minMinutes = 0,
  maxMinutes = DATE_TIME_PICKER_MAX_MINUTES,
  selectableMinMinutes,
  selectableMaxMinutes,
  disabledWeekdays,
  blockedDates,
  selectableStartDate,
  selectableStartDateTime,
  selectableEndDate,
  timelineStartMinutes = 0,
  timelineEndMinutes = DATE_TIME_PICKER_MAX_MINUTES,
  stepMinutes,
  showTimePicker = true,
  syncInitialTime = true,
}) => {
  const configuredStep = Number(stepMinutes);
  const STEP =
    Number.isFinite(configuredStep) && configuredStep > 0
      ? Math.max(1, Math.min(configuredStep, 60))
      : DATE_TIME_PICKER_STEP;
  const { t, language } = useLanguage();
  const locale = language === "de" ? "de-AT" : "en-US";
  const isNight = theme === "night";
  const rawSelectionMinMinutes =
    typeof selectableMinMinutes === "number" ? selectableMinMinutes : minMinutes;
  const rawSelectionMaxMinutes =
    typeof selectableMaxMinutes === "number" ? selectableMaxMinutes : maxMinutes;
  const selectionMinMinutes = Math.max(0, Math.min(rawSelectionMinMinutes, DATE_TIME_PICKER_MAX_MINUTES));
  const selectionMaxMinutes = Math.max(
    selectionMinMinutes,
    Math.min(rawSelectionMaxMinutes, DATE_TIME_PICKER_MAX_MINUTES)
  );
  const timelineMinMinutes = Math.max(
    0,
    Math.min(Number(timelineStartMinutes) || 0, DATE_TIME_PICKER_MAX_MINUTES)
  );
  const timelineMaxMinutes = Math.max(
    timelineMinMinutes + STEP,
    Math.min(
      Number(timelineEndMinutes) || DATE_TIME_PICKER_MAX_MINUTES,
      DATE_TIME_PICKER_MAX_MINUTES
    )
  );
  const timelineRangeMinutes = Math.max(STEP, timelineMaxMinutes - timelineMinMinutes);
  const initialBaseDate = useMemo(() => {
    return parseDateValue(initialDate) ?? new Date();
  }, [initialDate]);
  const selectableStartBound = useMemo(
    () => normalizeDateValue(selectableStartDate, false),
    [selectableStartDate]
  );
  const selectableStartDateTimeBound = useMemo(
    () => parseDateValue(selectableStartDateTime),
    [selectableStartDateTime]
  );
  const selectableEndBound = useMemo(
    () => normalizeDateValue(selectableEndDate, true),
    [selectableEndDate]
  );
  const resolvedInitialDate = useMemo(
    () =>
      resolveSelectableDate({
        value: initialBaseDate,
        minDate: selectableStartBound,
        maxDate: selectableEndBound,
        disabledWeekdays,
        blockedDates,
      }),
    [blockedDates, disabledWeekdays, initialBaseDate, selectableEndBound, selectableStartBound]
  );

  const [selectedDate, setSelectedDate] = useState(() => resolvedInitialDate);

  const [monthDays, setMonthDays] = useState([]);
  const [currentView, setCurrentView] = useState(
    () => new Date(resolvedInitialDate.getFullYear(), resolvedInitialDate.getMonth(), 1)
  );
  const [minutes, setMinutes] = useState(() => {
    const mins = resolvedInitialDate.getHours() * 60 + resolvedInitialDate.getMinutes();
    const initial = Math.min(Math.round(mins / STEP) * STEP, DATE_TIME_PICKER_MAX_MINUTES);
    return Math.max(selectionMinMinutes, Math.min(selectionMaxMinutes, initial));
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragDirection, setDragDirection] = useState(null);
  const [dragPercent, setDragPercent] = useState(null);
  const containerRef = useRef(null);
  const minutesRef = useRef(minutes);
  const selectedDateRef = useRef(selectedDate);
  const dragDirectionRef = useRef(dragDirection);
  const lastDragMinutesRef = useRef(minutes);
  const windTimeoutRef = useRef(null);
  const lastEmittedSelectionMsRef = useRef(null);

  useEffect(() => {
    minutesRef.current = minutes;
  }, [minutes]);

  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  useEffect(() => {
    dragDirectionRef.current = dragDirection;
  }, [dragDirection]);

  const clamp = useCallback((val, min, max) => Math.max(min, Math.min(max, val)), []);
  const clampPercent = useCallback((value) => Math.max(0, Math.min(100, value)), []);
  const clampRange = useCallback(
    (val) => clamp(val, selectionMinMinutes, selectionMaxMinutes),
    [clamp, selectionMinMinutes, selectionMaxMinutes]
  );
  const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
  const formatTime = (totalMin) => {
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return `${pad2(h)}:${pad2(m)}`;
  };
  const buildDateWithMinutes = (baseDate, totalMin) => {
    if (!(baseDate instanceof Date)) return null;
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    const nextDate = new Date(
      baseDate.getFullYear(),
      baseDate.getMonth(),
      baseDate.getDate(),
      h,
      m
    );
    return Number.isNaN(nextDate.getTime()) ? null : nextDate;
  };
  const emitSelection = useCallback(
    (baseDate, totalMin) => {
      if (typeof onSelectionChange !== "function") return;
      const nextDate = buildDateWithMinutes(baseDate, totalMin);
      if (!nextDate) return;
      const nextMs = nextDate.getTime();
      if (lastEmittedSelectionMsRef.current === nextMs) return;
      lastEmittedSelectionMsRef.current = nextMs;
      onSelectionChange(nextDate);
    },
    [onSelectionChange]
  );
  const clearWindTimeout = useCallback(() => {
    if (windTimeoutRef.current) {
      clearTimeout(windTimeoutRef.current);
      windTimeoutRef.current = null;
    }
  }, []);
  const updateDragDirection = useCallback((nextMinutes) => {
    const previousMinutes = lastDragMinutesRef.current;
    if (nextMinutes === previousMinutes) return;
    const nextDirection = nextMinutes > previousMinutes ? "right" : "left";
    if (dragDirectionRef.current !== nextDirection) {
      dragDirectionRef.current = nextDirection;
      setDragDirection(nextDirection);
    }
    lastDragMinutesRef.current = nextMinutes;
    clearWindTimeout();
    windTimeoutRef.current = setTimeout(() => {
      dragDirectionRef.current = null;
      setDragDirection(null);
      windTimeoutRef.current = null;
    }, 140);
  }, [clearWindTimeout]);
  const getClientX = useCallback((event) => {
    if (event.touches && event.touches.length > 0) {
      return event.touches[0].clientX;
    }
    if (event.changedTouches && event.changedTouches.length > 0) {
      return event.changedTouches[0].clientX;
    }
    return event.clientX;
  }, []);
  const preventDefaultIfNeeded = useCallback((event) => {
    const isTouchEvent = Boolean(event.touches || event.changedTouches);
    if (isTouchEvent && event.cancelable) {
      event.preventDefault();
    }
  }, []);
  const displayTime = formatTime(minutes);
  const percent = clampPercent(((minutes - timelineMinMinutes) / timelineRangeMinutes) * 100) / 100;
  const workingStartPercent = clampPercent(
    ((minMinutes - timelineMinMinutes) / timelineRangeMinutes) * 100
  );
  const workingEndPercent = clampPercent(
    ((maxMinutes - timelineMinMinutes) / timelineRangeMinutes) * 100
  );
  const workingWindowWidth = Math.max(workingEndPercent - workingStartPercent, 0);
  const hasWorkingWindow = maxMinutes > minMinutes;
  const monthFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: "long" }),
    [locale]
  );
  const monthLabel = (date) => monthFormatter.format(date);

  const dayNames = useMemo(() => {
    const monday = new Date(Date.UTC(2023, 0, 2)); // Monday, Jan 2 2023
    const formatter = new Intl.DateTimeFormat(locale, { weekday: "short" });
    return Array.from({ length: 7 }, (_, i) =>
      formatter.format(new Date(monday.getTime() + i * 86400000))
    );
  }, [locale]);

  const normalizedDisabledWeekdays = useMemo(
    () => (Array.isArray(disabledWeekdays) ? disabledWeekdays : []),
    [disabledWeekdays]
  );
  const disabledWeekdaysSet = useMemo(
    () => new Set(normalizedDisabledWeekdays.map((day) => Number(day))),
    [normalizedDisabledWeekdays]
  );
  const blockedDateSet = useMemo(() => buildBlockedDateSet(blockedDates), [blockedDates]);
  const getMinimumMinutesForDate = useCallback(
    (date) => {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return selectionMinMinutes;
      }

      let minForDate = selectionMinMinutes;
      if (selectableStartDateTimeBound) {
        const isSameLeadDate =
          date.getFullYear() === selectableStartDateTimeBound.getFullYear() &&
          date.getMonth() === selectableStartDateTimeBound.getMonth() &&
          date.getDate() === selectableStartDateTimeBound.getDate();
        if (isSameLeadDate) {
          const leadMinutes =
            selectableStartDateTimeBound.getHours() * 60 +
            selectableStartDateTimeBound.getMinutes();
          minForDate = Math.max(
            minForDate,
            Math.max(0, Math.min(leadMinutes, DATE_TIME_PICKER_MAX_MINUTES))
          );
        }
      }
      return minForDate;
    },
    [selectableStartDateTimeBound, selectionMinMinutes]
  );
  const isWithinSelectableDateRange = useCallback(
    (date) => {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
      if (selectableStartBound && date < selectableStartBound) return false;
      if (selectableEndBound && date > selectableEndBound) return false;
      return true;
    },
    [selectableEndBound, selectableStartBound]
  );

  // === helper: get minutes since midnight, rounded UP to nearest STEP ===
  const getNowMinutes = useCallback(() => {
    const now = new Date();
    const mins = now.getHours() * 60 + now.getMinutes();
    const roundedUp = Math.ceil(mins / STEP) * STEP;
    return clampRange(Math.min(roundedUp, DATE_TIME_PICKER_MAX_MINUTES));
  }, [STEP, clampRange]);

  // === Generate Month Days ===
  useEffect(() => {
    const year = currentView.getFullYear();
    const month = currentView.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const firstDay = new Date(year, month, 1);
    const startOffset = (firstDay.getDay() + 6) % 7;
    const totalCells = Math.ceil((daysInMonth + startOffset) / 7) * 7;
    const days = Array.from({ length: totalCells }, (_, i) => {
      const dayNum = i - startOffset + 1;
      const inCurrentMonth = dayNum > 0 && dayNum <= daysInMonth;
      const date = new Date(year, month, dayNum);
      const dateMid = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const isToday =
        inCurrentMonth &&
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();
      const isFutureOrToday = dateMid >= todayMid;
      const weekdayIndex = getMondayWeekdayIndex(date);
      const isClosed = inCurrentMonth && disabledWeekdaysSet.has(weekdayIndex);
      const dateKey = toDateKey(dateMid);
      const isBlocked = inCurrentMonth && Boolean(dateKey && blockedDateSet.has(dateKey));
      const isInSelectableRange = inCurrentMonth && isWithinSelectableDateRange(dateMid);
      const minimumMinutesForDate = getMinimumMinutesForDate(dateMid);
      const hasSlotForDate = selectionMaxMinutes >= minimumMinutesForDate;
      const isSelectable =
        inCurrentMonth
          ? isFutureOrToday && !isClosed && !isBlocked && isInSelectableRange && hasSlotForDate
          : false;
      return {
        daydate: inCurrentMonth ? dayNum : "",
        inCurrentMonth,
        isToday,
        showday: isSelectable,
        isClosed,
        isBlocked,
        selected: inCurrentMonth && date.toDateString() === selectedDate.toDateString(),
      };
    });
    setMonthDays(days);
  }, [
    blockedDateSet,
    currentView,
    disabledWeekdaysSet,
    getMinimumMinutesForDate,
    isWithinSelectableDateRange,
    selectedDate,
    selectionMaxMinutes,
  ]);

  const moveMonth = (dir) => {
    const d = new Date(currentView);
    d.setMonth(currentView.getMonth() + dir);
    setCurrentView(d);
  };

  const selectDay = (day) => {
    if (!day.inCurrentMonth || !day.showday || day.isClosed || day.isBlocked) return;
    const d = new Date(currentView.getFullYear(), currentView.getMonth(), day.daydate);
    if (!autoTimeOnDaySelect) {
      setSelectedDate(d);
      emitSelection(d, minutes);
      return;
    }

    const now = new Date();
    const nowMid = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const selectedMid = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const isTodaySelected = selectedMid.getTime() === nowMid.getTime();
    const openingMinutes = Math.ceil(getMinimumMinutesForDate(d) / STEP) * STEP;
    const boundedOpeningMinutes = Math.min(
      Math.max(openingMinutes, selectionMinMinutes),
      selectionMaxMinutes
    );
    const nextMinutes = isTodaySelected
      ? Math.max(getNowMinutes(), boundedOpeningMinutes)
      : boundedOpeningMinutes;

    setSelectedDate(d);
    setMinutes(nextMinutes);
    emitSelection(d, nextMinutes);
  };

  const percentFromEvent = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const x = clamp(clientX - rect.left, 0, rect.width);
    return rect.width === 0 ? 0 : x / rect.width;
  }, [clamp]);

  const minutesFromPercent = useCallback((p) => {
    const raw = Math.round(timelineMinMinutes + p * timelineRangeMinutes);
    const stepped = Math.round(raw / STEP) * STEP;
    return clampRange(stepped);
  }, [STEP, clampRange, timelineMinMinutes, timelineRangeMinutes]);

  const handlePointerDown = (e) => {
    preventDefaultIfNeeded(e);
    const clientX = getClientX(e);
    if (clientX == null) return;
    clearWindTimeout();
    dragDirectionRef.current = null;
    lastDragMinutesRef.current = minutesRef.current;
    setDragDirection(null);
    const p = percentFromEvent(clientX);
    setDragPercent(p);
    let newMinutes = minutesFromPercent(p);

    // 🚫 Prevent past times if today is selected
    const today = new Date();
    const isTodaySelected =
      selectedDate.getFullYear() === today.getFullYear() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getDate() === today.getDate();

    if (isTodaySelected) {
      const nowMin = getNowMinutes();
      const floorMin = Math.min(
        Math.max(getMinimumMinutesForDate(selectedDate), nowMin),
        selectionMaxMinutes
      );
      newMinutes = Math.max(newMinutes, floorMin);
    }

    const currentMinutes = minutesRef.current;
    if (newMinutes !== currentMinutes) {
      setMinutes(newMinutes);
      emitSelection(selectedDateRef.current, newMinutes);
      updateDragDirection(newMinutes);
    }
    setIsDragging(true);
  };

  const handlePointerMove = useCallback((e) => {
    if (!isDragging) return;
    preventDefaultIfNeeded(e);
    const clientX = getClientX(e);
    if (clientX == null) return;
    const p = percentFromEvent(clientX);
    setDragPercent(p);
    let newMinutes = minutesFromPercent(p);

    const today = new Date();
    const isTodaySelected =
      selectedDate.getFullYear() === today.getFullYear() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getDate() === today.getDate();

    if (isTodaySelected) {
      const nowMin = getNowMinutes();
      const floorMin = Math.min(
        Math.max(getMinimumMinutesForDate(selectedDate), nowMin),
        selectionMaxMinutes
      );
      newMinutes = Math.max(newMinutes, floorMin);
    }

    const currentMinutes = minutesRef.current;
    if (newMinutes !== currentMinutes) {
      setMinutes(newMinutes);
      emitSelection(selectedDateRef.current, newMinutes);
      updateDragDirection(newMinutes);
    }
  }, [
    emitSelection,
    getClientX,
    getMinimumMinutesForDate,
    getNowMinutes,
    isDragging,
    minutesFromPercent,
    percentFromEvent,
    preventDefaultIfNeeded,
    selectedDate,
    selectionMaxMinutes,
    updateDragDirection,
  ]);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
    setDragPercent(null);
    clearWindTimeout();
    dragDirectionRef.current = null;
    setDragDirection(null);
  }, [clearWindTimeout]);

  useEffect(() => {
    if (typeof onDragStateChange === "function") {
      onDragStateChange(isDragging);
    }
  }, [isDragging, onDragStateChange]);

  useEffect(() => {
    if (isDragging) return;

    const next = resolvedInitialDate;

    setSelectedDate((prev) => (prev.getTime() === next.getTime() ? prev : next));

    setCurrentView((prev) => {
      if (prev.getFullYear() === next.getFullYear() && prev.getMonth() === next.getMonth()) {
        return prev;
      }

      return new Date(next.getFullYear(), next.getMonth(), 1);
    });

    if (!syncInitialTime) return;

    const mins = next.getHours() * 60 + next.getMinutes();
    const target = Math.min(Math.round(mins / STEP) * STEP, DATE_TIME_PICKER_MAX_MINUTES);
    setMinutes((prev) => {
      const nextMin = Math.min(getMinimumMinutesForDate(next), selectionMaxMinutes);
      const clamped = clamp(target, nextMin, selectionMaxMinutes);
      if (clamped === prev) return prev;
      return clamped;
    });
  }, [
    STEP,
    clamp,
    getMinimumMinutesForDate,
    isDragging,
    resolvedInitialDate,
    selectionMaxMinutes,
    syncInitialTime,
  ]);

  useEffect(() => {
    setMinutes((prev) => {
      const nextMin = Math.min(getMinimumMinutesForDate(selectedDate), selectionMaxMinutes);
      const clamped = clamp(prev, nextMin, selectionMaxMinutes);
      if (clamped === prev) return prev;
      emitSelection(selectedDate, clamped);
      return clamped;
    });
  }, [clamp, emitSelection, getMinimumMinutesForDate, selectedDate, selectionMaxMinutes]);

  useEffect(() => {
    if (!showTimePicker) return;
    if (typeof onSelectionChange !== "function") return;
    const nextDate = buildDateWithMinutes(selectedDate, minutes);
    if (!nextDate) return;
    const nextMs = nextDate.getTime();
    const initialCandidate =
      initialDate instanceof Date ? initialDate : initialDate ? new Date(initialDate) : null;
    const initialMs =
      initialCandidate && !Number.isNaN(initialCandidate.getTime()) ? initialCandidate.getTime() : null;

    if (initialMs === nextMs) {
      lastEmittedSelectionMsRef.current = nextMs;
      return;
    }

    if (lastEmittedSelectionMsRef.current === nextMs) return;
    lastEmittedSelectionMsRef.current = nextMs;
    onSelectionChange(nextDate);
  }, [initialDate, minutes, onSelectionChange, selectedDate, showTimePicker]);

  useEffect(() => {
    if (!isDragging) return;
    const move = (e) => handlePointerMove(e);
    const up = () => handlePointerUp();
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    window.addEventListener("touchcancel", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
      window.removeEventListener("touchcancel", up);
    };
  }, [handlePointerMove, handlePointerUp, isDragging]);

  const stepBy = (delta) => {
    const minForDate = getMinimumMinutesForDate(selectedDate);
    const nextMinutes = clamp(
      minutes + delta,
      Math.min(minForDate, selectionMaxMinutes),
      selectionMaxMinutes
    );
    if (nextMinutes === minutes) return;
    setMinutes(nextMinutes);
    emitSelection(selectedDate, nextMinutes);
  };

  const buildDateWithTime = () => {
    const [h, m] = displayTime.split(":").map(Number);
    return new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      h,
      m
    );
  };

  const handleSave = () => {
    onSave?.(buildDateWithTime());
  };

  const formattedDate = useMemo(() => {
    if (!(selectedDate instanceof Date)) return "";
    const formatter = new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    const parts = formatter.formatToParts(selectedDate).reduce(
      (acc, part) => {
        acc[part.type] = part.value;
        return acc;
      },
      {}
    );
    return [parts.day, parts.month, parts.year].filter(Boolean).join(" ");
  }, [locale, selectedDate]);

  const durationLabel = useMemo(() => {
    if (duration >= 4) {
      return t("dateTimePicker.durationDay", "Day");
    }
    return formatDecimal(duration, 1);
  }, [duration, t]);

  const dragDirectionClass =
    isDragging && dragDirection ? `dragging-${dragDirection}` : "";
  const markerPercent = typeof dragPercent === "number" ? dragPercent : percent;
  const rootClass = `datepicker ${isNight ? "datepicker--night" : ""}`.trim();

  return (
    <>
    <div className={rootClass}>
      <div className="datepicker-header">
        <div className="datepicker-title">
          {t("dateTimePicker.title", "Select Date & Time")}
        </div>
        <div className="datepicker-subheader">
          <span className="subheader-date">{formattedDate}</span>
          <span className="subheader-time">{displayTime}</span>
        </div>
      </div>

      <div className="datepicker-body">
        {/* === CALENDAR === */}
        <div className="datepicker-calendar">
          <div className="calendar-header">
            <div className="goback" onClick={() => moveMonth(-1)}>
              <svg width="30" height="30">
                <path fill="none" stroke="#0DAD83" strokeWidth="3" d="M19,6 l-9,9 l9,9" />
              </svg>
            </div>
            <div className="current-month-container">
              {monthLabel(currentView)} {currentView.getFullYear()}
            </div>
            <div className="goforward" onClick={() => moveMonth(1)}>
              <svg width="30" height="30">
                <path fill="none" stroke="#0DAD83" strokeWidth="3" d="M11,6 l9,9 l-9,9" />
              </svg>
            </div>
          </div>

          <div className="calendar-day-header">
            {dayNames.map((d) => (
              <span key={d} className="day-label">{d}</span>
            ))}
          </div>

          <div className="calendar-grid">
            {monthDays.map((day, i) => (
              <div key={i} className="datecontainer">
                <div
                  className={`datenumber
                  ${!day.inCurrentMonth ? "old" : ""}
                  ${day.selected ? "day-selected" : ""}
                  ${day.isToday ? "today" : ""}
                  ${day.isClosed ? "closed" : ""}
                  ${day.isBlocked ? "blocked" : ""}
                  ${!day.showday ? "disabled" : ""}`}
                  onClick={() => selectDay(day)}
                  role="button"
                  aria-disabled={!day.showday || day.isClosed || day.isBlocked}
                >
                  {day.daydate}
                </div>
              </div>
            ))}
          </div>
        </div>

        {showTimePicker && (
        <div className="datepicker-side">
          {/* === TIME PICKER === */}
          <div className="timepicker-card">
          <div
            ref={containerRef}
            className="timeline-container"
            onMouseDown={handlePointerDown}
            onTouchStart={handlePointerDown}
            role="slider"
            aria-valuemin={Math.min(getMinimumMinutesForDate(selectedDate), selectionMaxMinutes)}
            aria-valuemax={selectionMaxMinutes}
            aria-valuenow={minutes}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "ArrowLeft") stepBy(-STEP);
              if (e.key === "ArrowRight") stepBy(STEP);
              if (e.key === "Home") {
                const nextMinutes = Math.min(
                  getMinimumMinutesForDate(selectedDate),
                  selectionMaxMinutes
                );
                if (nextMinutes !== minutes) {
                  setMinutes(nextMinutes);
                  emitSelection(selectedDate, nextMinutes);
                }
              }
              if (e.key === "End") {
                const nextMinutes = selectionMaxMinutes;
                if (nextMinutes !== minutes) {
                  setMinutes(nextMinutes);
                  emitSelection(selectedDate, nextMinutes);
                }
              }
            }}
          >
          {hasWorkingWindow && (
          <div className="working-window" aria-hidden="true">
            <span
              className="working-window__line"
              style={{
                left: `${workingStartPercent}%`,
                width: `${workingWindowWidth}%`,
              }}
            />
            <span
              className="working-window__line working-window__line--ghost"
              style={{
                left: `${workingStartPercent}%`,
                width: `${workingWindowWidth}%`,
              }}
            />
            <span
              className="working-window__edge working-window__edge--start"
              style={{ left: `${workingStartPercent}%` }}
            />
            <span
              className="working-window__edge working-window__edge--end"
              style={{ left: `${workingEndPercent}%` }}
            />
          </div>
        )}
              <div className="timeline-hour-ticks">
                {Array.from({ length: 25 }).map((_, i) => {
                  const tickMinutes = Math.min(
                    timelineMinMinutes + (timelineRangeMinutes / 24) * i,
                    DATE_TIME_PICKER_MAX_MINUTES
                  );
                  const isWithinWorkingHours =
                    hasWorkingWindow &&
                    tickMinutes >= selectionMinMinutes &&
                    tickMinutes <= selectionMaxMinutes;
                  return (
                    <span
                      key={i}
                      className={`tick ${(i % 6 === 0 ? "tick-lg" : "")} ${
                        isWithinWorkingHours ? "tick--active" : "tick--inactive"
                      }`}
                    />
                  );
                })}
              </div>
              <div
                className={`time-marker ${isDragging ? "dragging" : ""} ${dragDirectionClass}`.trim()}
                style={{ left: `${markerPercent * 100}%` }}
              >
              <div className="marker-wind" aria-hidden="true">
                <span />
                <span />
                <span />
                <span />
              </div>
              <div className="marker-label">{displayTime}</div>
            </div>
          </div>
        </div>
        </div>
        )}

        {/* === DURATION COUNTER === */}
        {showDurationControls && (
          <div className="duration-section">
            <div className="duration-wrapper">
              <h2>{t("dateTimePicker.durationHeading", "Duration in hours:")}</h2>
              <div className="counter">
                <button
                  type="button"
                  onClick={() => onDurationChange(Math.max(1, +(duration - 0.5).toFixed(1)))}
                  disabled={duration <= 1}
                >
                  –
                </button>

                <span>{durationLabel}</span>

                <button
                  type="button"
                  onClick={() =>
                    onDurationChange(
                      duration >= maxDuration
                        ? duration
                        : Math.min(maxDuration, +(duration + 0.5).toFixed(1))
                    )
                  }
                  disabled={duration >= maxDuration}
                >
                  +
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
      {
    showActions && (
	      <div className="datepicker-actions">
	        {actionsStatus ? (
	          <div className="datepicker-actions__status" role="status" aria-live="polite">
	            {actionsStatus}
	          </div>
	        ) : null}
	        <button
	          type="button"
	          className="datepicker-btn datepicker-btn--ghost"
	          onClick={() => onCancel?.()}
	        >
          {cancelLabel ?? t("dateTimePicker.actions.cancel", "Cancel")}
        </button>
        <button
          type="button"
          className="datepicker-btn datepicker-btn--primary"
          onClick={handleSave}
          disabled={isSaving || isSaveDisabled}
        >
          {isSaving
            ? t("dateTimePicker.actions.saving", "Saving…")
            : saveLabel ?? t("dateTimePicker.actions.save", "Save")}
        </button>
      </div>
    )
  }
    </>
  );
};

export default DateTimePicker;
